import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const APK_API_BASE = "https://api.experts4u.in/api/v1/apk";

const Apkupload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [latestInfo, setLatestInfo] = useState({ apkDownloadLink: "", id: "" });
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const controllerRef = useRef(null);

  const onFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".apk")) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Please select a .apk file." });
      return;
    }
    setSelectedFile(file);
  };

  const fetchLatest = async () => {
    try {
      setIsFetching(true);
      const response = await fetch(`${APK_API_BASE}/latest`, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch latest APK info");
      const data = await response.json();
      setLatestInfo({ apkDownloadLink: data.apkDownloadLink || "", id: data.id || "" });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: error.message || "Unable to load latest APK." });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchLatest();
  }, []);

  const uploadApk = async () => {
    if (!selectedFile) {
      Swal.fire({ icon: "info", title: "Select file", text: "Choose an .apk file to upload." });
      return;
    }
    try {
      setIsUploading(true);
      setUploadProgress(0);
      controllerRef.current = new AbortController();
      const formData = new FormData();
      formData.append("ApkFile", selectedFile, selectedFile.name);

      const response = await axios.post(`${APK_API_BASE}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || selectedFile.size || 0;
          if (!total) return;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
          setUploadProgress(percentCompleted);
        },
        signal: controllerRef.current.signal,
      });
      const resultText = typeof response.data === "string" ? response.data : (response.data?.message || "APK uploaded successfully");
      setUploadProgress(100);
      Swal.fire({ icon: "success", title: "Uploaded", text: resultText });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchLatest();
    } catch (error) {
      if (axios.isCancel?.(error) || error?.name === "CanceledError" || error?.message === "canceled") {
        Swal.fire({ icon: "info", title: "Cancelled", text: "Upload cancelled" });
      } else {
        const message = error?.response?.data?.message || error.message || "Failed to upload APK";
        Swal.fire({ icon: "error", title: "Error", text: message });
      }
    } finally {
      setIsUploading(false);
      controllerRef.current = null;
      setTimeout(() => setUploadProgress(0), 800);
    }
  };

  const cancelUpload = () => {
    try {
      controllerRef.current?.abort();
    } catch (_) {}
  };

  const downloadLatest = () => {
    window.open(`${APK_API_BASE}/download/latest`, "_blank");
  };

  const copyLink = async () => {
    const linkToCopy = "https://api.experts4u.in/api/v1/apk/download/latest";
    try {
      await navigator.clipboard.writeText(linkToCopy);
      Swal.fire({ icon: "success", title: "Copied", text: "Download link copied to clipboard" });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Copy failed", text: "Unable to copy link" });
    }
  };

  const dropZoneOnClick = () => fileInputRef.current?.click();

  const onDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".apk")) {
      Swal.fire({ icon: "warning", title: "Invalid file", text: "Please drop a .apk file." });
      return;
    }
    setSelectedFile(file);
  };

  const onDragOver = (event) => event.preventDefault();

  return (
    <main className="app-main">
      <div className="wrapper">
        <div className="page">
          <div className="page-inner">
            <div className="container-fluid">
              <header className="page-title-bar">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                  <h2 className="mb-2 mb-md-0" style={{ fontWeight: 700 }}>APK Manager</h2>
                </div>
              </header>

              <div className="row">
                <div className="col-lg-7 mb-4">
                  <div className="card" style={{ overflow: "hidden", border: "none", boxShadow: "0 10px 25px rgba(0,0,0,0.08)", borderRadius: 14 }}>
                    <div
                      className="card-header text-white"
                      style={{
                        background: "linear-gradient(135deg, #5b86e5 0%, #36d1dc 100%)",
                        border: "none",
                      }}
                    >
                      <h4 className="mb-0">Upload new APK</h4>
                    </div>
                    <div className="card-body">
                      <div
                        onClick={dropZoneOnClick}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        role="button"
                        className="w-100 d-flex flex-column align-items-center justify-content-center text-center"
                        style={{
                          border: "2px dashed #a1b0ff",
                          borderRadius: 14,
                          padding: 28,
                          background: "#f8faff",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{ width: 56, height: 56, borderRadius: 14, background: "#eaf2ff" }} className="d-flex align-items-center justify-content-center mb-3">
                          {/* Upload icon */}
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 16V4" stroke="#4a6cf7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 9L12 4L17 9" stroke="#4a6cf7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M20 16V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V16" stroke="#4a6cf7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: "#2a2f45" }}>Drag & drop your .apk here</div>
                          <div style={{ color: "#6b7280", fontSize: 14 }} className="mt-1">
                            or click to browse
                          </div>
                        </div>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".apk"
                        className="form-control mt-3"
                        onChange={onFileChange}
                      />

                      {selectedFile && (
                        <div className="d-flex align-items-center justify-content-between mt-3 p-3" style={{ background: "#f3f6ff", borderRadius: 12 }}>
                          <div className="d-flex align-items-center">
                            <div className="mr-2" style={{ width: 10, height: 10, background: "#4a6cf7", borderRadius: 999 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{selectedFile.name}</div>
                              <div style={{ color: "#6b7280", fontSize: 12 }}>{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</div>
                            </div>
                          </div>
                          <button disabled={isUploading} onClick={uploadApk} className="btn btn-primary">
                            {isUploading ? "Uploading..." : "Upload APK"}
                          </button>
                        </div>
                      )}

                      {(isUploading || uploadProgress > 0) && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: 12, color: "#6b7280" }}>
                            <span>Uploading</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div className="progress" style={{ height: 10, background: "#e5e7eb", borderRadius: 999 }}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${uploadProgress}%`, background: "linear-gradient(90deg, #36d1dc 0%, #5b86e5 100%)" }}
                              aria-valuenow={uploadProgress}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                          {isUploading && (
                            <div className="d-flex justify-content-end mt-2">
                              <button onClick={cancelUpload} className="btn btn-light">Cancel</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 mb-4">
                  <div className="card" style={{ overflow: "hidden", border: "none", boxShadow: "0 10px 25px rgba(0,0,0,0.08)", borderRadius: 14 }}>
                    <div className="card-header bg-white">
                      <h4 className="mb-0" style={{ fontWeight: 700 }}>Latest APK</h4>
                    </div>
                    <div className="card-body">
                      <div className="mb-2" style={{ color: "#6b7280", fontSize: 13 }}>Current public link</div>
                      <div className="d-flex align-items-center p-3" style={{ background: "#f9fafb", borderRadius: 12 }}>
                        <div className="flex-grow-1 text-truncate" title={latestInfo.apkDownloadLink} style={{ fontFamily: "monospace" }}>
                          {isFetching ? "Loading..." : latestInfo.apkDownloadLink || "Not available"}
                        </div>
                        <button onClick={copyLink} className="btn btn-light ml-2" title="Copy link">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="9" y="9" width="13" height="13" rx="2" stroke="#111827" strokeWidth="2" />
                            <rect x="3" y="3" width="13" height="13" rx="2" stroke="#111827" strokeWidth="2" />
                          </svg>
                        </button>
                      </div>

                      <div className="d-flex mt-3">
                        <button onClick={fetchLatest} disabled={isFetching} className="btn btn-outline-primary mr-2">
                          {isFetching ? "Refreshing..." : "Refresh"}
                        </button>
                        <button onClick={downloadLatest} className="btn btn-success">Download latest</button>
                      </div>

                      {latestInfo.id && (
                        <div className="mt-3" style={{ color: "#6b7280", fontSize: 12 }}>ID: {latestInfo.id}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Apkupload;